/**
 * Pausenende beim Server anmelden — für die Mitteilung in der Tasche.
 *
 * Bewusst nicht über den Sync (siehe `rest_timers` im Schema): Die Anmeldung
 * muss vor dem Pausenende ankommen. Klappt sie nicht, sagt die App das unter
 * dem Timer, statt so zu tun, als käme eine Mitteilung.
 */

import { apiFetch } from '../../api.js';

export type RestPushState = 'off' | 'pending' | 'on' | 'failed';

/** Die Pause melden. `true`, wenn der Server sie angenommen hat. */
export async function announceRest(
  id: string,
  endsAt: number,
  label: string | null,
): Promise<boolean> {
  try {
    await apiFetch(`/rest-timer/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ endsAt: new Date(endsAt).toISOString(), label }),
    });
    return true;
  } catch {
    return false;
  }
}

/** Übersprungene Pause absagen — sonst klingelt es mitten im nächsten Satz. */
export function cancelRest(id: string): void {
  void fetchCancel(id);
}

async function fetchCancel(id: string): Promise<void> {
  try {
    await apiFetch(`/rest-timer/${id}`, { method: 'DELETE' });
  } catch {
    // 204 hat keinen Körper, und ohne Netz gibt es nichts mehr zu retten: Die
    // Mitteilung kommt dann eben. Lästig, aber kein Datenverlust.
  }
}

/** Sind Mitteilungen auf diesem Gerät überhaupt erlaubt? */
export function notificationsAllowed(): boolean {
  return typeof Notification !== 'undefined' && Notification.permission === 'granted';
}

export interface RestEntry {
  exerciseId: string;
  name: string;
  /** Geplante Sätze. */
  sets: number;
  /** Supersatz-Block; fehlt er, steht die Übung allein. */
  block?: number;
}

/**
 * Was nach der Pause dran ist — für den Text der Mitteilung.
 *
 * Erst die laufende Übung, solange ihr Sätze fehlen, dann die nächste
 * unvollständige in der Reihenfolge des Plans. Im Supersatz beginnt die
 * nächste Runde mit der ersten Übung des Blocks, die am wenigsten Sätze hat. Ist alles erledigt: `null`, und
 * die Mitteilung sagt nur „weiter".
 */
export function nextSetLabel(
  entries: readonly RestEntry[],
  doneByExercise: ReadonlyMap<string, number>,
  current: string,
): string | null {
  const open = (entry: RestEntry) => (doneByExercise.get(entry.exerciseId) ?? 0) < entry.sets;

  const here = entries.find((entry) => entry.exerciseId === current);
  const block =
    here?.block === undefined ? [] : entries.filter((entry) => entry.block === here.block);
  if (block.length > 1) {
    const done = (entry: RestEntry) => doneByExercise.get(entry.exerciseId) ?? 0;
    const next = block
      .filter(open)
      .reduce<RestEntry | null>(
        (best, entry) => (!best || done(entry) < done(best) ? entry : best),
        null,
      );
    if (next) return next.name;
  } else if (here && open(here)) return here.name;

  const start = entries.findIndex((entry) => entry.exerciseId === current);
  const ordered = start < 0 ? entries : [...entries.slice(start + 1), ...entries.slice(0, start)];
  return ordered.find(open)?.name ?? null;
}
